
import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatasourceUpstreamDatasourcesQuery,
  DatasourceUpstreamDatasourcesQueryVariables
} from '../../../gen-src/queries/DatasourceUpstreamDatasourcesQuery';
import {
  DatasourceUpstreamDatasourcesWithoutPersonalSpaceQuery,
  DatasourceUpstreamDatasourcesWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/DatasourceUpstreamDatasourcesWithoutPersonalSpaceQuery';
import { DatasourcesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragment';
import { DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  UpstreamDatasourcesFilteredByFieldQuery,
  UpstreamDatasourcesFilteredByFieldQueryVariables
} from '../../../gen-src/queries/UpstreamDatasourcesFilteredByFieldQuery';
import {
  UpstreamDatasourcesFilteredByFieldWithoutPersonalSpaceQuery,
  UpstreamDatasourcesFilteredByFieldWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/UpstreamDatasourcesFilteredByFieldWithoutPersonalSpaceQuery';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceUpstreamDatasourcesQueryGql } from '../../queries/DatasourceUpstreamDatasourcesQueryGql';
import { DatasourceUpstreamDatasourcesWithoutPersonalSpaceQueryGql } from '../../queries/DatasourceUpstreamDatasourcesWithoutPersonalSpaceQueryGql';
import { UpstreamDatasourcesFilteredByFieldQueryGql } from '../../queries/UpstreamDatasourcesFilteredByFieldQueryGql';
import { UpstreamDatasourcesFilteredByFieldWithoutPersonalSpaceQueryGql } from '../../queries/UpstreamDatasourcesFilteredByFieldWithoutPersonalSpaceQueryGql';
import {
  DatasourcesDisplayGrid,
  DatasourcesDisplayGridRowData,
  getRowDataForDatasource,
  getRowDataForDatasourceWithoutPersonalSpace,
} from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = DatasourceUpstreamDatasourcesQueryVariables;
type QueryResults = DatasourceUpstreamDatasourcesQuery;
type QueryResultListNode = DatasourcesDisplayGridRowDataFragment;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatasourceUpstreamDatasourcesWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatasourceUpstreamDatasourcesWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = UpstreamDatasourcesFilteredByFieldQueryVariables;
type QueryResultsWithFilter = UpstreamDatasourcesFilteredByFieldQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = UpstreamDatasourcesFilteredByFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = UpstreamDatasourcesFilteredByFieldWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0]
    && resultData.publishedDatasources[0].upstreamDatasourcesConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0]
    && resultData.publishedDatasources[0].upstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].upstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].upstreamDatasourcesConnection;
}

export type DatasourceUpstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function DatasourceUpstreamDatasourcesQueryGrid(props: DatasourceUpstreamDatasourcesQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatasourcesLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFieldId ? (
      // Query with Selected Field as Filter
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={UpstreamDatasourcesFilteredByFieldQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatasourceUpstreamDatasourcesQueryGql}
        partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={UpstreamDatasourcesFilteredByFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatasourceUpstreamDatasourcesWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  );
}
